<template>
    <div class="payment-plan-popup">
        <BaseModalWithLogo class="payment-plan-popup__container">
            <div class="payment-plan-popup__form">
                <span class="payment-plan-popup__title">Choose Your Plan</span>
                <span class="payment-plan-popup__subtitle">Select the Audience Key plan that best suits your
                    needs.</span>
                <div class="payment-plan-popup__plan-type"
                    v-if="selectedPaymentPlanType">
                    <BasePillsLikeTabBar :options="paymentPlanTypes"
                        v-model="selectedPaymentPlanType" />
                    <p class="payment-plan-popup__plan-type-subtitle">{{ selectedPaymentPlanType.description }}</p>
                </div>
                <div class="payment-plan-popup__list" v-if="plansListToshow">
                    <div v-for="plan in plansListToshow"
                        :key="plan.name"
                        class="payment-plan-popup__list-item"
                        @click="selectPlan(plan)">
                        <PaymentPlanListItem :name="plan.name"
                            :price="plan.price"
                            :description="plan.description"
                            :is-selected="selectedPlan === plan"
                            :includes-trial="plan.includes_trial" />
                    </div>
                </div>
                <div class="payment-plan-popup__list-extra-info">
                    <span class="payment-plan-popup__list-extra-info-title">Agencies</span>
                    <p class="payment-plan-popup__list-extra-info-description">Looking to use Audience Key across
                        multiple
                        projects? <br /> Contact us to learn about our Agency partner program or
                        <a target="_blank" href="https://calendly.com/audiencekey/30min"
                            class="payment-plan-popup__list-extra-info-link">schedule a call</a>.
                    </p>
                    <img class="payment-plan-popup__list-extra-info-icon"
                        src="@/assets/ak-logo-badge--solid-dark.svg" />
                </div>
            </div>
            <div class="payment-plan-popup__info">
                <template v-if="selectedPlan">
                    <div class="payment-plan-popup__info-header">
                        <span class="payment-plan-popup__info-header-title">Selected Plan</span>
                        <span class="payment-plan-popup__info-header-plan-name">
                            {{ selectedPlan.name }}
                        </span>
                        <Price :price="selectedPlan.price" />
                    </div>
                    <div class="payment-plan-popup__info-features">
                        <span class="payment-plan-popup__info-features-list-title">Included in {{
                            selectedPlan.name
                            }}</span>
                        <div class="payment-plan-popup__info-features-list">
                            <div v-for="feature in selectedPlan.features"
                                :key="feature"
                                class="payment-plan-popup__info-features-item">
                                <BaseMaterialDesignIcon icon="check" />
                                {{ feature }}
                            </div>
                        </div>
                        <a target="_blank" href="https://audiencekey.com/pricing/"
                            class="payment-plan-popup__info-features-link">See all plan features</a>
                        <BaseButton button-style="dark-gray"
                            class="payment-plan-popup__info-features-button"
                            type="button"
                            @click="confirmPaymentPlanSelection">
                            Next: Payment
                            <BaseMaterialDesignIcon icon="arrowRight" />
                        </BaseButton>
                    </div>
                </template>
            </div>
        </BaseModalWithLogo>
    </div>
</template>

<script>
import BaseModalMixins from '@/common/BaseModalMixins'
import PaymentPlanListItem from '@/features/agency/PaymentPlanListItem'
import Price from '@/features/agency/common/Price'
import { mapState, mapActions } from 'vuex'
export default {
    name: 'PaymentPlanPopup',
    mixins: [BaseModalMixins],
    components: {
        PaymentPlanListItem,
        Price
    },
    data() {
        return {
            selectedPlan: null,
            includeGridPrice: false,
            paymentPlanTypes: [
                {
                    label: 'Standard',
                    value: 'standard',
                    description: 'For non-ecommerce projects not tracking organic product grids and merchant store listings'
                },
                {
                    label: 'E-Commerce',
                    value: 'merchant',
                    description: 'Includes organic product grid and merchant rank tracking alongside standard SEO tracking'
                }
            ],
            selectedPaymentPlanType: null
        }
    },
    created() {
        this.getPaymentPlans()
        this.getStripeClientSecret()
        this.selectedPaymentPlanType = this.paymentPlanTypes[0]
    },
    watch: {
        plansListToshow() {
            this.selectedPlan = this.plansListToshow[0]
        },

    },
    computed: {
        ...mapState('agency', ['paymentPlans']),
        structuredPaymentPlans() {
            const plans = this.paymentPlans.reduce((acc, plan) => {
                if (plan.type) {
                    acc[plan.type].push(plan)
                }
                return acc
            }, {
                standard: [],
                merchant: []
            })
            plans.standard.sort((a, b) => a.price - b.price)
            plans.merchant.sort((a, b) => a.price - b.price)
            return plans
        },
        selectedPlanPrice() {
            if (!this.structuredPaymentPlans) {
                return 0
            }
            const selectedPlan = this.plansListToshow[this.selectedPlanIndex]
            if (this.includeGridPrice) {
                return selectedPlan.gridPrice + selectedPlan.price
            }
            return selectedPlan.price
        },
        plansListToshow() {
            if (this.selectedPaymentPlanType) {
                return this.structuredPaymentPlans[this.selectedPaymentPlanType.value]
            }
            return this.structuredPaymentPlans.standard
        }
    },
    methods: {
        ...mapActions('agency', ['selectPaymentPlan', 'getPaymentPlans', 'getStripeClientSecret']),
        selectPlan(plan) {
            this.selectedPlan = plan
        },
        confirmPaymentPlanSelection() {
            // this.selectPaymentPlan({ 
            //     paymentPlanId: this.structuredPaymentPlans[this.selectedPlanIndex].id, 
            //     userId: this.currentUser._id
            // });
            this.$emit('payment-plan-selected', this.selectedPlan);
        }
    }
}
</script>

<style lang="scss" scoped>
.payment-plan-popup {
    height: 90vh;
    min-height: 510px;
    display: flex;
    flex-direction: column;

    &__container {
        flex: 1 1 auto;
        display: flex;
        overflow: hidden;
        position: relative;
    }

    &__form {
        flex: 0 0 560px;
        padding: 48px 40px 32px 48px;
        box-sizing: border-box;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    &__info {
        flex: 1;
        background-color: $rfx-light-turquoise;
    }

    &__title {
        display: block;
        font-size: 24px;
        font-weight: 700;
        color: $rfx-dark-march;
        margin-bottom: 12px;
    }

    &__subtitle {
        display: block;
        font-size: 16px;
        color: $rfx-text-dark-mid-contrast;
        margin-bottom: 24px;
    }

    &__plan-type {
        display: flex;
        flex-direction: column;
    }

    &__plan-type-subtitle {
        margin: 8px 0 28px;
        font-size: 14px;
        line-height: 143%;
        color: $rfx-text-dark-mid-contrast;
        font-weight: 400;
    }

    &__compare-link {
        font-size: 12px;
        color: $rfx-text-dark-mid-contrast;
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }

    &__list {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        gap: 12px;
        flex: 1;
        margin-right: -25px;
        padding-right: 25px;
    }

    &__list-extra-info {
        position: relative;
        margin-top: 40px;
        flex: 0 0 auto;
        background-color: $rfx-light-turquoise;
        padding: 16px 56px 16px 16px;
        border-radius: 8px;

        &::before {
            content: '';
            position: absolute;
            top: -20px;
            left: 50%;
            transform: translateX(-50%);
            width: 120px;
            height: 1px;
            background-color: $rfx-medium-steel;
        }
    }

    &__list-extra-info-title {
        display: block;
        font-size: 20px;
        color: $rfx-dark-march;
        margin-bottom: 8px;
    }

    &__list-extra-info-description {
        font-size: 14px;
        line-height: 143%;
        font-weight: 400;
        color: $rfx-text-dark-mid-contrast;
        margin: 0;
    }

    &__list-extra-info-link {
        color: $rfx-dark-march;
        white-space: nowrap;
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }

    &__list-extra-info-icon {
        position: absolute;
        top: 16px;
        right: 16px;
        width: 26px;
        height: 26px;
    }

    &__info {
        padding: 88px 32px 104px;
        position: relative;
        display: flex;
        flex-direction: column;
    }

    &__info-header {
        padding: 0 16px 20px;
        display: flex;
        flex-direction: column;
        border-bottom: 1px solid $rfx-medium-steel;
        margin-bottom: 20px;
    }

    &__info-header-title {
        display: block;
        font-size: 14px;
        color: $rfx-text-dark-mid-contrast;
        margin-bottom: 20px;
        margin-left: -16px;
    }

    &__info-header-plan-name {
        display: block;
        font-size: 24px;
        font-weight: 600;
        color: $rfx-dark-march;
        margin-bottom: 8px;
    }

    &__grid {
        padding: 0 16px 20px;
        border-bottom: 1px solid $rfx-medium-steel;
        margin-bottom: 20px;
    }

    &__grid-control {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 0 -16px 4px 0;
        user-select: none;
        cursor: pointer;
    }

    &__grid-label {
        font-size: 14px;
        font-weight: 500;
        line-height: 133%;
        color: $rfx-dark-march;
    }

    &__grid-description {
        font-size: 12px;
        color: $rfx-text-dark-mid-contrast;
    }

    &__info-features {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        flex: 1;
        gap: 16px;
        color: $rfx-dark-march;
        font-size: 14px;
    }

    &__info-features-list-title {
        display: block;
        font-size: 14px;

    }

    &__info-features-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
        overflow-y: auto;
    }

    &__info-features-item {
        display: flex;
        align-items: center;
        gap: 8px;

        .material-design-icon {
            font-size: 18px;
        }
    }

    &__info-features-link {
        display: block;
        font-size: 14px;
        color: $rfx-dark-march;
        text-decoration: underline;
        margin-left: 26px;

        &:hover {
            text-decoration: none;
        }
    }

    &__info-features-button {
        position: absolute;
        bottom: 32px;
        right: 32px;
        display: flex;
        align-items: center;
        gap: 4px;
        color: white;
        padding-right: 16px !important;

        .material-design-icon {
            color: inherit;
        }
    }

}
</style>